import type {
  DateTimeMode,
  LengthValidation,
  PublicFormField,
  TextFieldDefinition,
} from "../../types.ts";
import {
  appendHelpText,
  applyNumberValidation,
  applyTextAreaLengthValidation,
  createControlLabel,
  createFieldScaffold,
  createTextArea,
  createTextInput,
} from "../shared.ts";
import type { NativeInputType, RenderContext, RenderField } from "../types.ts";

const dateTimeNativeType: Record<DateTimeMode, NativeInputType> = {
  both: "datetime-local",
  date: "date",
  time: "time",
};

/**
 * Renders a CONFIRM field whose control mirrors its target field.
 *
 * The confirm control derives its native input shape and length/number rules
 * from the target (including a textarea for a multiline TEXT target) and
 * inherits the target's required flag. It records the confirm/target identity in
 * dataset attributes so managed cross-field validation can match the two values
 * without exposing either answer.
 */
export const renderConfirmField = (
  field: RenderField<"CONFIRM">,
  context: RenderContext,
): HTMLElement => {
  const scaffold = createFieldScaffold(field, context);
  const target = context.fieldsById.get(field.configJson.targetFieldId);
  const required = target?.required ?? false;
  const label = createControlLabel(field.label, required, scaffold.inputId);

  const control = createConfirmControl(field, scaffold.inputId, scaffold.helpId, target);
  control.dataset.confirmFieldId = field.id;
  control.dataset.confirmTargetId = field.configJson.targetFieldId;

  scaffold.wrapper.append(label, control);
  appendHelpText(scaffold, field);

  return scaffold.wrapper;
};

const createConfirmControl = (
  field: RenderField<"CONFIRM">,
  inputId: string,
  helpId: string | undefined,
  target: PublicFormField | undefined,
): HTMLInputElement | HTMLTextAreaElement => {
  const required = target?.required ?? false;

  if (target?.type === "TEXT" && target.configJson?.multiline) {
    const definition = confirmDefinition(field, required, target.validationJson);
    const textarea = createTextArea(definition, inputId, helpId);
    applyTextAreaLengthValidation(textarea, definition);

    return textarea;
  }

  const definition = confirmDefinition(field, required, null);
  const inputType = nativeTypeForTarget(target);
  const input = createTextInput(definition, inputId, helpId, inputType);

  applyTargetNativeRules(input, target);

  return input;
};

// A synthetic TEXT input definition using the confirm field's own name and
// placeholder but the target's required flag and length rules. It never carries
// a config, so it is a plain input-like field for the shared control builders.
const confirmDefinition = (
  field: RenderField<"CONFIRM">,
  required: boolean,
  validationJson: LengthValidation | null,
): TextFieldDefinition => {
  return {
    columnIndex: field.columnIndex,
    configJson: null,
    helpText: field.helpText,
    id: field.id,
    key: field.key,
    label: field.label,
    placeholder: field.placeholder,
    required,
    rowIndex: field.rowIndex,
    type: "TEXT",
    validationJson,
  };
};

const nativeTypeForTarget = (target: PublicFormField | undefined): NativeInputType => {
  switch (target?.type) {
    case "EMAIL":
      return "email";
    case "PHONE":
      return "tel";
    case "WEBSITE":
      return "url";
    case "NUMBER":
      return "number";
    case "DATETIME":
      return dateTimeNativeType[target.configJson.mode];
    default:
      return "text";
  }
};

const applyTargetNativeRules = (
  input: HTMLInputElement,
  target: PublicFormField | undefined,
): void => {
  if (!target) {
    return;
  }

  switch (target.type) {
    case "TEXT":
    case "EMAIL":
    case "PHONE":
      applyInputLengthRules(input, target.validationJson);
      break;
    case "WEBSITE":
      applyInputLengthRules(input, target.validationJson);
      if (target.validationJson?.httpsOnly) {
        input.pattern = "https://.*";
        input.title = "Use an HTTPS URL.";
      }
      break;
    case "NUMBER":
      applyNumberValidation(input, target.validationJson);
      break;
    case "DATETIME":
      if (target.configJson.mode === "time" || target.configJson.mode === "both") {
        input.step = "60";
      }
      break;
    default:
      break;
  }
};

const applyInputLengthRules = (input: HTMLInputElement, rules: LengthValidation | null): void => {
  if (!rules) {
    return;
  }

  if (rules.minLength !== undefined) {
    input.minLength = rules.minLength;
  }

  if (rules.maxLength !== undefined) {
    input.maxLength = rules.maxLength;
  }
};
